import {
  TUserAccountInfo,
  TUserProfileInfo,
  User,
  TDashboardPageData,
  TClientDetails,
  TClientDetailsStackInfo,
  TClientDetailsClientInfo,
  TClientDetailsPaymentsInfo,
  TClientsTableItem,
  Product,
  Tariff,
  PartnersTableItem,
  TAccountInfo,
} from 'types/state';
import { createClientProductMock } from 'utils/clientProductUtils';
import { getRuntimeUid } from 'utils/getRuntimeUid';
import { createPartnerProfileMock } from 'utils/partnerProfileUtils';
import { createPartnerMock } from 'utils/partnerUtils';
import { createProdStackInfoMock } from 'utils/prodStackInfoUtils';
import { createDemoStackInfoMock } from 'utils/demoStackInfoUtils';
import { createRandomBoolean } from 'utils/randomBooleanUtils';
import { createUniqueRuntimeNumber } from 'utils/uniqueRuntimeNumberUtils';
import { createCardPaymentMock } from 'utils/cardPaymentUtils';
import { createM8LegalRequisitesMock } from 'utils/m8LegalRequisitesUtils';
import { createPartnerFinanceMonthReportMock } from './partnerFinanceMonthReportUtils';
import { createClientFinancesMock } from './clientFInancesUtils';

export const ADMIN_EMAIL_MOCK = 'admin@mail.ru';

export const CLIENT_EMAIL_MOCK = 'client@mail.ru';

export const PARTNER_EMAIL_MOCK = 'partner@mail.ru';

export const PARTNER_CANDIDATE_EMAIL_MOCK = 'prepartner@mail.ru';

export const PROMO_EMAIL_MOCK = 'promo@mail.ru';

export const createPartnerAccountInfoMock = (): TAccountInfo => ({
  email: PROMO_EMAIL_MOCK,
  id: createUniqueRuntimeNumber(),
});

export const createUserAccountInfoMock = (): TUserAccountInfo => ({
  email: ADMIN_EMAIL_MOCK,
  roles: ['ADMIN'],
  authProvider: 'EMAIL',
  id: 1,
});

export const createUserProfileInfoMock = (): TUserProfileInfo => ({
  company: 'company',
  firstName: 'Cool',
  lastName: 'Guy',
  middleName: 'Guyevich',
  phone: '+376 55 333-44-32',
  orgStaffCount: createRandomBoolean() ? 'medium' : null,
  position: 'cleaner',
  location: 'BY',
  resident: true,
});

export const createUserMock = (): User => ({
  accountInfo: createUserAccountInfoMock(),
  profileInfo: createUserProfileInfoMock(),
  partnerProfile: createPartnerProfileMock(),
  partnerFinances: {
    balanceAmount: 1200,
    paidAmount: 1400,
    earnedAmount: 200,
  },
  partnerFinanceMonthReports: [
    createPartnerFinanceMonthReportMock(2),
    createPartnerFinanceMonthReportMock(1),
    createPartnerFinanceMonthReportMock(3),
  ],
  partnerCode: 'XY57',
  cancelledClientsCount: 12,
});

export const createDashboardPageDataMock = (): TDashboardPageData => ({
  accountInfo: createUserAccountInfoMock(),
  promoAccountsInfo: [createPartnerAccountInfoMock()],
});

export const createClientDetailsStackInfoMock = (): TClientDetailsStackInfo => ({
  demoStackInfo: createDemoStackInfoMock(),
  prodStackInfo: createProdStackInfoMock(),
  managersCount: null,
});

export const createClientDetailsClientInfoMock = (): TClientDetailsClientInfo => ({
  id: 1,
  fullName: 'Александров Александр Александрович',
  company: 'company',
  position: 'position',
  staff: createRandomBoolean() ? 'small' : null,
  phone: 'phone',
  email: 'email@mail.com',
  domain: 'domain',
  location: 'BY',
  updatedOn: new Date().toISOString(),
  registeredOn: new Date().toISOString(),
  resident: true,
  partnerCode: null,
  archived: false,
});

export const createPaymentsInfoMock = (): TClientDetailsPaymentsInfo => ({
  balance: 100500,
  transactions: [
    {
      type: 'PAYMENT',
      payment: createCardPaymentMock(getRuntimeUid()),
    },
  ],
});

export const createClientDetailsMock = (): TClientDetails => ({
  stackInfo: createClientDetailsStackInfoMock(),
  clientInfo: createClientDetailsClientInfoMock(),
  userProfileInfo: createUserProfileInfoMock(),
  paymentsInfo: createPaymentsInfoMock(),
  legalRequesites: createM8LegalRequisitesMock(),
  partner: createPartnerMock(),
  m8BillRows: [
    {
      id: createUniqueRuntimeNumber(),
      type: 'DEBIT',
      paymentDate: new Date().toISOString(),
      monthFor: new Date().toISOString(),
      payed: false,
      amount: 100,
      amountCurrency: 'USD',
      billedAmount: null,
      billedCurrency: null,
      comment: 'A43',
    },
    {
      id: createUniqueRuntimeNumber(),
      type: 'BY_CARD',
      paymentDate: new Date().toISOString(),
      monthFor: null,
      payed: false,
      amount: 520,
      amountCurrency: 'USD',
      billedAmount: 1320,
      billedCurrency: 'BYN',
      comment: null,
    },
    {
      id: createUniqueRuntimeNumber(),
      type: 'REFUND',
      paymentDate: new Date().toISOString(),
      monthFor: null,
      payed: true,
      amount: 330,
      amountCurrency: 'BYN',
      billedAmount: null,
      billedCurrency: null,
      comment: 'Возврат средств',
    },
    {
      id: createUniqueRuntimeNumber(),
      type: 'CASHLESS',
      paymentDate: new Date().toISOString(),
      monthFor: null,
      payed: true,
      amount: 500,
      amountCurrency: 'EUR',
      billedAmount: null,
      billedCurrency: null,
      comment: null,
    },
  ],
  clientProducts: [createClientProductMock(), createClientProductMock()],
  finances: createClientFinancesMock(),
});

export const createClientsTableItem = (id: number): TClientsTableItem => ({
  id: id,
  company: `Компания ${id}`,
  fullName: 'Имя Фамилия Отчество',
  phone: '+375 44 555 44 22',
  position: 'Должность',
  orgStaffCount: createRandomBoolean() ? 'small' : null,
  balance: -150,
  email: 'myemail@mail.com',
  updatedOn: new Date().toISOString(),
  registeredOn: new Date().toISOString(),
  prodStack: null,
  demoStack: null,
});

export const createProduct = (id: number): Product => ({
  id: id,
  type: 'PRODUCT_TYPE',
  defaultAmount: Math.random(),
  defaultPartnerComission: 0,
  name: `Название продукта ${id}`,
  status: 'ACTIVE',
  createdAt: new Date().toISOString(),
  activeTariff: null,
  latestTariff: null,
});

export const createTariffMock = (id: number): Tariff => ({
  id,
  cost: 100500,
  createdAt: new Date().toISOString(),
  expiresAt: new Date().toISOString(),
  startsAt: new Date().toISOString(),
});

export const createPartnersTableItemMock = (id: number): PartnersTableItem => ({
  id,
  fullName: 'Полное Имя Партнера',
  email: PARTNER_EMAIL_MOCK,
  phone: '+375 33 555-66-44',
  status: createRandomBoolean() ? 'ACTIVE' : 'CANDIDATE',
  clientsCount: 25,
  earnedAmount: 3500,
  paidAmount: 780,
  balance: 0,
  latestUpdateDate: new Date().toISOString(),
});
